import React, { useEffect, useState, useCallback } from 'react';
import { saveAs } from 'file-saver';
import useForm from '@utils/useForm';
import { Helpers } from '@utils/helpers';
import { GAAviva } from '@utils/gaAviva';
import { useHistory } from 'react-router-dom';
import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { useTheme } from 'src/theme';
import { get } from 'lodash';
import { DeclarationForm } from '@models/DPI/Declaration.model';
import { updateDeclaration, updateReadDocs, completeStep, updatePdfPath } from '@redux/slices/DPI/step4Slice';
import axios, { postDeclarationPDF, postSavingMyInfo } from '@api/index';
import { IMyInfoQuestionRequest } from '@models/MyInfoRequest.model';
import { setLoadingState } from '@redux/slices/loadingSlice';

import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import IconDownload from '@assets/icon/download.svg';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import Text from '@components/Text';
import CheckboxList from '@components/CheckboxList';
import Checkbox from '@components/Checkbox';
import Link from '@components/Link';
import TextCollapsible from '@components/TextCollapsible';
import ContactBox from '@components/ContactBox';
import InformationBox from '@components/InformationBox';
import Button from '@components/Button';
import BulletList from '@components/BulletList';
import Bullet from '@components/Bullet';
import Divider from '@components/Divider';
import RadioButton from '@components/RadioButton';
import RadioButtonGroup from '@components/RadioButtonGroup';
import { validate } from './service';
import ContainerBlock from '@components/ContainerBlock';
import { fileLinkGen } from '@static/staticConstents';

const page = 'Declaration';

const convertDeclaration = (values: DeclarationForm) => {
  const convertValue = (value: boolean | undefined) => value === true ? 'Y' : 'N';
  return `1,${convertValue(values.policy)}|` +
    `2,${convertValue(values.summary)}|` +
    `3,${convertValue(values.term)}|` +
    `8,${convertValue(values.ped)}|` +
    `9,${convertValue(values.dpi)}|` +
    `5,${convertValue(values.assured)}|` +
    `6,${convertValue(values.benefit)}|` +
    `10,${convertValue(values.completedDPI)}|` +
    `11,${convertValue(values.usedEstimator)}|` +
    `12,${convertValue(values.usedBudgetCalculator)}|` +
    `13,${convertValue(values.visitedCompareFirst)}|` +
    `14,${convertValue(values.considerDiffDPI)}`
}

const convertConsent = (values: any) => {
  let customerConsent = `SMS,${values['sms'] ? 'Y' : 'N'}|Phone,${values['phone'] ? 'Y' : 'N'}|Mail,${values['mail'] ? 'Y' : 'N'}`;
  return customerConsent;
}

const trackOnload = (values: any): void => {
  const consents = [
    values['sms'] && 'SMS',
    values['phone'] && 'Phone',
    values['mail'] && 'Mail',
  ].filter(o => o);
  const GA = GAAviva.getInstance();
  let { acquisition, form } = GA.digitalData;
  form.name = 'Declaration';
  acquisition.customer.marketingOptIn = consents;
  GA.trackPageLoad({ form, acquisition });
}

const trackFormSubmmit = (error: string = ''): void => {
  const GA = GAAviva.getInstance();
  if (error === '') {
    GA.trackFormSuccess({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = error;
  GA.trackFormFailed({ form });
}

export interface DeclarationProps { }

function Declaration(props: DeclarationProps) {
  console.log(`Aviva-${page}`);
  const history = useHistory();
  const theme = useTheme();
  const dispatch = useAvivaDispatch();
  const sqsQuotation = useAvivaSelector(state => state.dpiStep1.sqsQuotation);
  const transactionId= useAvivaSelector(state=>state.dpiStep1.transactionId)
  const contactInformation = useAvivaSelector(state => state.staticData.contactInformation);
  const basicDetails = useAvivaSelector(state => state.dpiStep1.basicDetails);
  const retrievedMyInfo = useAvivaSelector(state => state.dpiStep2.retrievedMyInfo);

  const quoteRawRequest = useAvivaSelector(state => state.dpiStep1.quoteRawRequest);
  const coverage = useAvivaSelector(state => state.dpiStep1.coverage);
  const myInfoForm = useAvivaSelector(state => state.dpiStep2.myInfoForm);
  const initialValue = useAvivaSelector(state => state.dpiStep4.declaration);
  const readDocs = useAvivaSelector(state => state.dpiStep4.readDocs);
  const isStep3Completed = useAvivaSelector(state => state.dpiStep3.isCompleted);

  const { values, formValid, handleChange, patchValue } = useForm(new DeclarationForm({...initialValue,usedEstimator: true }), () => { }, validate);

  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [isReadPolicy, setIsReadPolicy] = useState<boolean>(false);
  const [isReadProduct, setIsReadProduct] = useState<boolean>(false);
  const [isReadTerms, setIsReadTerms] = useState<boolean>(false);
  const [isReadDeclare, setIsReadDeclare] = useState<boolean>(false);
  const [isReadDPI, setIsReadDPI] = useState<boolean>(false);
  const [coverPolicy, setCoverPolicy] = useState<string>('./docs/illustration.pdf');
  const [productSummary, setProductSummary] = useState<string>('');
  const [terms, setTerms] = useState<string>('');


  const getCoverPolicy = useCallback(async () => {
    dispatch(setLoadingState(true));
    //const payload: SQSPocketRequest = createSQSPayload({ basicDetails, coverage });
    const name = get(myInfoForm, 'fullName', '');
    const lifeprofiles: any = get(quoteRawRequest, 'lifeprofiles[0]');
    const savedRequest = { ...quoteRawRequest, lifeprofiles: [{ ...lifeprofiles, name: Helpers.capitalizeName(name) }] };
    const payload = { ...savedRequest};
    console.log(`check customerId: ${retrievedMyInfo?.id}`);
    postDeclarationPDF({ ...payload, customerId: retrievedMyInfo?.id, document: ['cp', 'bi'] }).then(res => {
      console.debug('postDeclarationPDF', res);
      const policyUrl = get(res, 'data');
      if (policyUrl) {
        dispatch(updatePdfPath(policyUrl));  // save filename
      }
      const url = './pdf/' + String(policyUrl).replace('/api/v1/pdf/', '');
      console.log('Cover Policy', url);
      setCoverPolicy(url);
      dispatch(setLoadingState(false));
    }, err => {
      console.error(err);
      dispatch(setLoadingState(false));
      window.location.reload();
    });
  }, [quoteRawRequest, dispatch, myInfoForm]);

  const downloadCoverPolicy = (url: any) => {
    console.log('policy full path', window.location.origin + window.location.pathname.replace('/index.html', '') + url.replace('./', '/'));
    const fullPath = window.location.origin + window.location.pathname.replace('/index.html', '') + url.replace('./', '/');
    console.log('fullPath', fullPath);
    axios({
      url: fullPath,
      method: 'GET',
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
      responseType: 'blob', // important
    }).then((response) => {
      let extension = 'pdf';
      let tempFileName = `cover_policy_illustration`
      let fileName = `${tempFileName}.${extension}`;
      var blob = new Blob([response.data as unknown as BlobPart], { type: "application/pdf" });
      saveAs(blob, fileName);
    });
    return false;
  }


  const updateMyInfo = () => {
    const id = retrievedMyInfo?.id || 0;
    const declaration = convertDeclaration(values);
    let customerConsent = convertConsent(values);
    console.log('customerConsent', customerConsent);
    const payload: IMyInfoQuestionRequest = { id, customerConsent, declaration, transactionId };
    postSavingMyInfo(payload).then(res => {
      console.debug('postSavingMyInfo', res);
      history.push('/dpi/review');
    }, error => {
      console.error(error);
      history.push('/dpi/review');
    })
  }

  const onClickContinue = () => {
    dispatch(updateDeclaration(values));
    dispatch(completeStep(true));
    dispatch(updateReadDocs({ isReadPolicy, isReadProduct, isReadTerms, isReadDeclare, isReadDPI }));
    trackOnload(values);
    trackFormSubmmit();
    updateMyInfo();

  }

  const onBack = useCallback((step: number = 1) => {
    history.push(`/dpi/about-you?step=${step}`)
  }, [history])

  const gotoContact = () => {
    dispatch(completeStep(false));
    history.push('/dpi/contact')
  }

  const checkboxHandleChange = (e: any) => {
    const { value, name } = e.target
    console.log(e.target, { [name]: value });
    patchValue({ [name]: value });
  }


  useEffect(() => {
    if (!isPageLoaded) {
      if (!isStep3Completed && process.env.REACT_APP_INJECT_CHECK !== 'true') {
        dispatch(completeStep(false));
        onBack(0);
      }
      if (readDocs) {
        const { isReadPolicy, isReadProduct, isReadTerms, isReadDeclare, isReadDPI } = readDocs;
        setIsReadDeclare(isReadDeclare);
        setIsReadPolicy(isReadPolicy);
        setIsReadProduct(isReadProduct);
        setIsReadTerms(isReadTerms);
        setIsReadDPI(isReadDPI);
      }
      if (basicDetails && coverage) {
        // const payload: SQSPocketRequest = createSQSPayload({ basicDetails, coverage });
        // postDeclarationPDF(payload).then(res => {
        //   console.debug('postDeclarationPDF', { res });
        // });
        getCoverPolicy();
        const isCIRider = sqsQuotation?.isCIRider;
        if (isCIRider) {
          setProductSummary(fileLinkGen("dpi","DirectsalesDPI-PS-CI.pdf"));
          setTerms(fileLinkGen("dpi","DirectsalesDPI-Terms-CI.pdf"));
        } else {
          setProductSummary(fileLinkGen("dpi","DirectsalesDPI-PS.pdf"));
          setTerms(fileLinkGen("dpi","DirectsalesDPI-Terms.pdf"))//'./docs/dpi/Terms.pdf'); //fileLinkGen("dpi","Terms.pdf")
        }
      }
      trackOnload(values);
      setIsPageLoaded(true);
    }

  }, [coverage, basicDetails, getCoverPolicy, sqsQuotation, readDocs, isPageLoaded, setIsPageLoaded, dispatch, isStep3Completed, onBack, values]);
  return (
    <Container>
      <ContainerGrid>

        <FullGrid>
          <MultiSteps steps={5} step={4} label="Declaration" />
          <Heading> Declaration </Heading>
        </FullGrid>
        <LeftGrid>
          <Heading variant="label" margin="0 0 30px"> Please read and agree to all of the following to proceed. </Heading>
          <InformationBox hasClose>
            <Text>Please ensure that you have <b>clicked on the links</b> and read the documents fully before selecting the checkboxes.</Text>
          </InformationBox>
          <CheckboxList margin="0 0 40px">
            <Checkbox name="policy" margin="0 0 30px" checked={values['policy']} onChange={handleChange} disabled={!isReadPolicy}>
              <Text> I have downloaded, fully read and understood the <Link target="_blank" rel="noopener noreferrer" url={coverPolicy} hasDownload={true} downloadCallback={() => { downloadCoverPolicy(coverPolicy); }} callback={() => { setIsReadPolicy(true) }} isCheckClicked isClicked={isReadPolicy} text="cover page and policy illustration" icon={IconDownload} slotIcon="end" /></Text>
            </Checkbox>
            <Checkbox name="summary" margin="0 0 30px" checked={values['summary']} onChange={handleChange} disabled={!isReadProduct}>
              <Text> I have downloaded, fully read and understood the <Link download="product-summary" text="product summary" target="_blank" rel="noopener noreferrer" url={productSummary} callback={() => { setIsReadProduct(true) }} isClicked={isReadProduct} isCheckClicked icon={IconDownload} slotIcon="end" /></Text>
            </Checkbox>
            <Checkbox name="term" margin="0 0 30px" checked={values['term']} onChange={handleChange} disabled={!isReadTerms}>
              <Text> I have downloaded, fully read and understood the <Link download="terms-and-conditions" text="policy terms and conditions" target="_blank" rel="noopener noreferrer" url={terms} callback={() => { setIsReadTerms(true) }} isClicked={isReadTerms} isCheckClicked icon={IconDownload} slotIcon="end" /></Text>
            </Checkbox>
            <Checkbox name="ped" margin="0 0 30px" checked={values['ped']} onChange={handleChange} disabled={!isReadDeclare}>
              <Text> I have downloaded, fully read and agreed to the statements described in this <Link download="declaration" target="_blank" rel="noopener noreferrer" url={fileLinkGen("dpi","DirectsalesDPI-Declaration.pdf")} callback={() => { setIsReadDeclare(true) }} isClicked={isReadDeclare} isCheckClicked text="online purchase declaration" icon={IconDownload} slotIcon="end" /></Text>
            </Checkbox>
            <Checkbox name="dpi" margin="0 0 30px" checked={values['dpi']} onChange={handleChange} disabled={!isReadDPI}>
              <Text> I have downloaded, fully read and agreed to the statements described in this <Link download="factsheet" target="_blank" rel="noopener noreferrer" url={fileLinkGen("dpi","DirectsalesDPI-Factsheet.pdf")} isCheckClicked isClicked={isReadDPI} text="DPI factsheet" callback={() => { setIsReadDPI(true) }} icon={IconDownload} slotIcon="end" /></Text>
            </Checkbox>

            <Checkbox name="assured" checked={values['assured']} onChange={handleChange} disabled={!isReadPolicy || !isReadProduct || !isReadTerms || !isReadDeclare || !isReadDPI}>
              <Text> I am not a Politically Exposed Person (PEP) or a family member/close associate of a PEP.</Text>
              <TextCollapsible labelMore="Know more" labelLess="Know less" margin="10px 0">
                <Text margin="0 0 10px">
                  “PEP” means a natural person who holds or has held prominent public functions, whether locally or internationally, which includes the roles held by a head of state, a head of government, government ministers, senior civil or public servants, senior judicial or military officials, senior executives of state owned corporations, senior political party officials, members of the legislature and senior management of international organisations.                  <br />
                  <br />
                  “Family Member” means a parent, step-parent, child, step-child, adopted child, spouse, sibling, step-sibling and adopted sibling of the PEP.                  <br />
                  <br />
                  “Close Associate” means a natural person who is closely connected to a PEP, either socially or professionally.                </Text>
              </TextCollapsible>
            </Checkbox>
            <Checkbox name="benefit" checked={values['benefit']} onChange={handleChange} disabled={!isReadPolicy || !isReadProduct || !isReadTerms || !isReadDeclare || !isReadDPI}>
              <Text> There is no beneficial ownership arrangement under this application.</Text>
              <TextCollapsible labelMore="Know more" labelLess="Know less" margin="10px 0" expanded>
                <Text margin="0 0 10px">
                  “Beneficial Owner” means the natural person who ultimately owns or controls a customer or the natural person on whose behalf a transaction is conducted or business relations are established and includes any person who exercises ultimate effective control over a legal person or legal arrangement.
                </Text>
              </TextCollapsible>
            </Checkbox>
            <Checkbox name="completed" checked={values['completed']} onChange={handleChange} disabled={!isReadPolicy || !isReadProduct || !isReadTerms || !isReadDeclare || !isReadDPI}><Text> I have completed and disclosed fully and truthfully all the information requested for in this application. </Text></Checkbox>
            <Checkbox name="understand" margin="0 0 30px" checked={values['understand']} onChange={handleChange} disabled={!isReadPolicy || !isReadProduct || !isReadTerms || !isReadDeclare || !isReadDPI}>
              <Text> I understand that I will receive my policy, any endorsements and communications electronically once they are ready. I understand that I may log on to MySinglife to opt for my preferred document option and may switch between e-documents and printed documents.</Text>
            </Checkbox>
          </CheckboxList>

          <Heading variant="sub" margin="0 0 15px"> Direct Purchase Insurance (DPI) Checklist </Heading>
          <Divider size={1} color={theme.colors.grey[700]} margin="0 0 30px" />
          <CheckboxList margin="0 0 40px" >
            <Checkbox name="completedDPI" checked={values['completedDPI']} onChange={handleChange} disabled={!isReadPolicy || !isReadProduct || !isReadTerms || !isReadDeclare || !isReadDPI}>
              <div>
                <Text margin="0 0 10px 0"> I have </Text>
                <BulletList>
                  <Bullet>Read and understood the DPI Fact Sheet </Bullet>
                  <Bullet>Read and understood the Policy Illustration and product summary, including any coverage exclusions of the DPI</Bullet>
                  <Bullet>Declared all pre-existing medical conditions in the Proposal Form</Bullet>
                  <Bullet>Disclosed all existing life insurance policies that I own, or am in the process of applying for in the Proposal Form</Bullet>
                  <Bullet>Declared my current financial situation, such as my income and expenses in the Proposal Form</Bullet>
                  <Bullet>Completed and disclosed fully and truthfully all the information requested in the Proposal Form and any supplementary questionnaire(s)</Bullet>
                  <Bullet>Decided to buy the DPI, without seeking any advice from any financial advisory representative</Bullet>
                </BulletList>
              </div>
            </Checkbox>
          </CheckboxList>
          
          <Heading variant="sub" margin="0 0 15px"> You are encouraged to go through the following before buying a DPI </Heading>
          <Divider size={1} color={theme.colors.grey[700]} margin="0 0 30px" />
          {
            (values['usedEstimator'] === 'N' || values['usedBudgetCalculator'] === 'N' || values['visitedCompareFirst'] === 'N' || values['considerDiffDPI'] === 'N') && (
              <InformationBox hasClose>
                <Text>You are encouraged to use the Insurance Estimator and Budget Calculator, and to compare DPI on <Link text="www.comparefirst.sg" url="http://www.comparefirst.sg" target="_blank" rel="noopener noreferrer" fontWeight="bold"></Link></Text>
              </InformationBox>
            )
          }
          <Heading variant="label" fontWeight="normal" margin="0 0 10px"> Have you ... </Heading>
          <ContainerBlock margin="0 0 30px" marginTablet="0 0 60px">
            <BulletList>
              {/* <Bullet> <Text>Used the  <Link target="_blank" rel="noopener noreferrer" text="Insurance Estimator" url="https://www.cpf.gov.sg/eSvc/Web/Schemes/InsuranceEstimator/InsuranceEstimator"></Link> to calculate the amount of life insurance coverage you would need?</Text></Bullet>
              <RadioButtonGroup name="usedEstimator" value={values['usedEstimator']} onChange={handleChange}>
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup> */}
              <Bullet><Text>Used the <Link target="_blank" rel="noopener noreferrer" text="Budget Calculator" url="https://www.moneysense.gov.sg/financial-tools/budget-calculator"></Link> to check if the premium that you will pay is affordable based on your current income and expenditure?</Text></Bullet>
              <RadioButtonGroup name="usedBudgetCalculator" value={values['usedBudgetCalculator']} onChange={handleChange}>
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Bullet>
                <Text>Visted <Link text="Comparefirst" target="_blank" rel="noopener noreferrer" url="http://www.comparefirst.sg"></Link> to compare the features and premiums of Direct Purchase Insurance (DPI) and other types of life policies?</Text>
              </Bullet>
              <RadioButtonGroup name="visitedCompareFirst" value={values['visitedCompareFirst']} onChange={handleChange}>
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>
              <Bullet><Text>Considered the different types of DPI and other types of life policies that are available, and whether the life policy that you intend to purchase is suitable for your financial circumstances and needs?</Text></Bullet>
              <RadioButtonGroup name="considerDiffDPI" value={values['considerDiffDPI']} onChange={handleChange}>
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>
            </BulletList>
          </ContainerBlock>
          <Heading variant="sub" margin="0 0 15px">Stay in touch with us!</Heading>
          <Divider size={1} color={theme.colors.grey[700]} margin="0 0 30px" />
          <Text margin="0 0 30px">We’ll update you on special marketing offers, promotions, and information about Singlife’s products and services which may be of interest to you. For details, please refer to <Link target="_blank" rel="noopener noreferrer" url="https://singlife.com/en/pdpa#dpp" text="Singlife’s data protection policy"></Link>. </Text>
          <Text margin="0 0 15px">You consent to receiving marketing updates via:</Text>
          <CheckboxList margin="0 0 24px">
            <Checkbox variant="border" name="mail" onChange={e => checkboxHandleChange(e)}> <Text>Mail or Email</Text> </Checkbox>
            <Checkbox variant="border" name="sms" onChange={e => checkboxHandleChange(e)}> <Text>Messages on any messaging platform (including SMS)</Text> </Checkbox>
            <Checkbox variant="border" name="phone" onChange={e => checkboxHandleChange(e)}> <Text>Telephone Call</Text> </Checkbox>
          </CheckboxList>
          <Text margin="0 0 40px">If you wish to withdraw your contest, you may do so at <Link target="_blank" rel="noopener noreferrer" url="https://singlife.com/en/pdpa#contact-us" text="Singlife’s website"></Link> at anytime.
          </Text>
          <InformationBox hasClose margin="0 0 40px">
            <Text>If you’re unable to complete this Declaration, please contact us <Link text="here" variant="button" onClick={gotoContact}></Link></Text>
          </InformationBox>
          <Button label="Continue" disabled={!formValid} onClick={onClickContinue} margin="0 0 20px" />
          <Button variant="secondary" onClick={() => { onBack(1) }} label="Back" margin="0 0 40px" />
        </LeftGrid>
        <EmptyGrid></EmptyGrid>
        <RightGrid>
          <ContactBox items={contactInformation} />
        </RightGrid>

      </ContainerGrid>
    </Container>
  );
};

export default Declaration;